import React, { useState, useEffect } from 'react';
import { X, Bell, Clock, Calendar, Save, RotateCcw } from 'lucide-react';
import emailNotificationPrefService from '../services/emailNotificationPrefService';
import toast from 'react-hot-toast';
import ResponsiveModal from './ResponsiveModal';
import { SaveButton, CancelButton, DeleteButton } from './CommonButtons';

const EmailNotificationPrefs = ({ isOpen, onClose, dataroomUuid, dataroomName }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState({
    use_global: true,
    enabled: true,
    frequency: '24h',
    time_of_day: '09:00'
  });
  const [originalPreferences, setOriginalPreferences] = useState(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  // Fetch current preferences when modal opens
  useEffect(() => {
    if (isOpen && dataroomUuid) {
      fetchPreferences();
    }
  }, [isOpen, dataroomUuid]);

  const fetchPreferences = async () => {
    setLoading(true);
    try {
      const response = await emailNotificationPrefService.getPreferences(dataroomUuid);
      const prefs = response.data || response;
      setPreferences(prefs);
      setOriginalPreferences(prefs);
    } catch (error) {
      // If no preferences exist, use defaults
      const defaults = {
        use_global: true,
        enabled: true,
        frequency: '24h',
        time_of_day: '09:00'
      };
      setPreferences(defaults);
      setOriginalPreferences(defaults);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validate time format if not using global settings
    if (!preferences.use_global && preferences.time_of_day) {
      if (!emailNotificationPrefService.validateTimeFormat(preferences.time_of_day)) {
        toast.error('Please enter a valid time in HH:MM format (e.g., 09:00)');
        return;
      }
    }

    setSaving(true);
    try {
      await emailNotificationPrefService.updatePreferences(dataroomUuid, preferences);
      toast.success('Email notification preferences updated successfully');
      setOriginalPreferences(preferences);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setIsResetting(true);
    try {
      await emailNotificationPrefService.deletePreferences(dataroomUuid);
      toast.success('Preferences reset to default');
      // Reset to default values
      const defaults = {
        use_global: true,
        enabled: true,
        frequency: '24h',
        time_of_day: '09:00'
      };
      setPreferences(defaults);
      setOriginalPreferences(defaults);
      setShowResetModal(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reset preferences');
    } finally {
      setIsResetting(false);
    }
  };

  const handleTimeChange = (e) => {
    const value = e.target.value;
    // Allow typing and validate on save
    setPreferences(prev => ({
      ...prev,
      time_of_day: value
    }));
  };

  const hasChanges = JSON.stringify(preferences) !== JSON.stringify(originalPreferences);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-primary-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Email Notification Preferences
              </h2>
              <p className="text-xs text-gray-500">
                {dataroomName ? `Configure for ${dataroomName}` : 'Configure email notifications'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto flex-1">
          {loading ? (
            <div className="flex justify-center py-6">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Use Global Settings */}
              <div className="bg-blue-50 rounded-lg p-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.use_global}
                    onChange={(e) => setPreferences(prev => ({
                      ...prev,
                      use_global: e.target.checked
                    }))}
                    className="mt-1 w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 text-sm">
                      Use Global Settings
                    </div>
                    <p className="text-xs text-gray-600 mt-0.5">
                      When enabled, this dataroom will use your account's global email notification settings
                    </p>
                  </div>
                </label>
              </div>

              {/* Custom Settings */}
              <div className={`space-y-3 ${preferences.use_global ? 'opacity-50 pointer-events-none' : ''}`}>
                {/* Enable/Disable Notifications */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Bell className={`w-4 h-4 ${preferences.enabled ? 'text-green-600' : 'text-gray-400'}`} />
                    <div>
                      <div className="font-medium text-gray-900 text-sm">
                        Email Notifications
                      </div>
                      <p className="text-xs text-gray-600">
                        Receive email updates about this dataroom
                      </p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.enabled}
                      onChange={(e) => setPreferences(prev => ({
                        ...prev,
                        enabled: e.target.checked
                      }))}
                      disabled={preferences.use_global}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </div>

                {/* Frequency */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    <Calendar className="w-3 h-3 inline mr-1.5" />
                    Notification Frequency
                  </label>
                  <select
                    value={preferences.frequency}
                    onChange={(e) => setPreferences(prev => ({
                      ...prev,
                      frequency: e.target.value
                    }))}
                    disabled={preferences.use_global || !preferences.enabled}
                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {emailNotificationPrefService.getFrequencyOptions().map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Time of Day */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    <Clock className="w-3 h-3 inline mr-1.5" />
                    Preferred Time
                  </label>
                  <input
                    type="time"
                    value={preferences.time_of_day || '09:00'}
                    onChange={handleTimeChange}
                    disabled={preferences.use_global || !preferences.enabled}
                    className="w-full border border-gray-300 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 py-1.5 rounded-lg px-3 text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-0.5">
                    Time when you prefer to receive notification summaries (24-hour format)
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t px-4 py-3 bg-gray-50 flex-shrink-0">
          <div className="flex items-center justify-between">
            <DeleteButton
              onClick={() => setShowResetModal(true)}
              disabled={saving || loading}
              icon={RotateCcw}
            >
              Reset to Default
            </DeleteButton>
            <div className="flex gap-2">
              <CancelButton
                onClick={onClose}
                disabled={saving}
              />
              <SaveButton
                onClick={handleSave}
                disabled={saving || loading || !hasChanges}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </SaveButton>
            </div>
          </div>
        </div>
      </div>

      {/* Reset Confirmation Modal */}
      <ResponsiveModal
        isOpen={showResetModal}
        onClose={() => setShowResetModal(false)}
        title="Reset to Default Settings"
        size="small"
      >
        <p className="text-sm text-gray-500 mb-6">
          Are you sure you want to reset email notification preferences to default settings?
          <span className="block mt-2 text-amber-600">
            This will revert all custom settings and use global preferences.
          </span>
          <span className="block mt-2">This action cannot be undone.</span>
        </p>
        <div className="flex justify-end gap-3 pt-4 border-t">
          <CancelButton
            onClick={() => setShowResetModal(false)}
          />
          <DeleteButton
            onClick={handleReset}
            disabled={isResetting}
            icon={RotateCcw}
          >
            {isResetting ? 'Resetting...' : 'Reset to Default'}
          </DeleteButton>
        </div>
      </ResponsiveModal>
    </div>
  );
};

export default EmailNotificationPrefs;